import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { printTicket } from "../api/printer";
import { GenerateRegisterTicket } from "../api/ticket";
import {
  canCloseRegister,
  closeRegister,
  getOpenRegister,
  getRegisterArqueo,
} from "../api/cash";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserContext } from "../context/user";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const QUICK_AMOUNTS = [5, 10, 20, 50];
const KEY_HEIGHT = "h-[clamp(2.5rem,7dvh,3.5rem)]";
const KEY_TEXT = "text-[clamp(1.125rem,3.5dvh,1.625rem)]";

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)}€`;
}

/**
 * Teclado numérico para contar el cajón.
 *
 * El TPV se usa en terminales táctiles que no siempre tienen teclado físico, así
 * que el importe se teclea aquí. Trabaja sobre un string para no perder los
 * decimales mientras se escribe.
 */
function AmountKeypad({ expected, onChange, disabled }) {
  const press = (event, action) => {
    event.stopPropagation();
    if (disabled) return;

    onChange((current) => {
      switch (action.type) {
        case "clear":
          return "";
        case "back":
          return current.slice(0, -1);
        case "dot":
          if (current.includes(".")) return current;
          return current === "" ? "0." : `${current}.`;
        case "exact":
          return expected > 0 ? expected.toFixed(2) : current;
        case "add":
          return (Number(current || 0) + action.amount).toFixed(2);
        default: {
          // dígitos: como mucho dos decimales
          const decimals = current.split(".")[1];
          if (decimals && decimals.length >= 2) return current;
          return current === "0" ? action.value : current + action.value;
        }
      }
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1.5">
        {QUICK_AMOUNTS.map((amount) => (
          <Button
            key={amount}
            type="button"
            variant="outline"
            disabled={disabled}
            className={`flex-1 bg-white text-neutral-900 ${KEY_HEIGHT}`}
            onClick={(event) => press(event, { type: "add", amount })}
          >
            +{amount}€
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`flex-1 bg-white text-neutral-900 ${KEY_HEIGHT}`}
          onClick={(event) => press(event, { type: "clear" })}
        >
          C
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {new Array(9).fill(1).map((_, index) => (
          <Button
            key={index}
            type="button"
            variant="outline"
            disabled={disabled}
            className={`bg-white font-bold text-neutral-900 active:scale-95 ${KEY_HEIGHT} ${KEY_TEXT}`}
            onClick={(event) => press(event, { type: "digit", value: String(index + 1) })}
          >
            {index + 1}
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`bg-white font-bold text-neutral-900 ${KEY_HEIGHT} ${KEY_TEXT}`}
          onClick={(event) => press(event, { type: "dot" })}
        >
          .
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`bg-white font-bold text-neutral-900 ${KEY_HEIGHT} ${KEY_TEXT}`}
          onClick={(event) => press(event, { type: "digit", value: "0" })}
        >
          0
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label="Borrar"
          className={`bg-white ${KEY_HEIGHT}`}
          onClick={(event) => press(event, { type: "back" })}
        >
          <Icon
            icon="mdi:backspace-outline"
            className="text-[clamp(1.375rem,4dvh,1.875rem)]"
          />
        </Button>
      </div>

      <Button
        type="button"
        variant="outline"
        disabled={disabled || expected <= 0}
        className={`bg-white font-semibold text-blue-700 ${KEY_HEIGHT}`}
        onClick={(event) => press(event, { type: "exact" })}
      >
        Importe exacto
      </Button>
    </div>
  );
}

function differenceLabel(difference) {
  if (Math.abs(difference) < 0.005) return "La caja cuadra";
  return difference < 0 ? "Faltan" : "Sobran";
}

/**
 * Arqueo y cierre de caja.
 *
 * Enseña lo que debería haber (fondo de apertura más lo cobrado en efectivo)
 * antes de cerrar nada, para que el conteo sea una comparación y no un acto a
 * ciegas. Los cobros con tarjeta o transferencia no están en el cajón y no se
 * suman.
 *
 * Cerrar deja constancia del turno: el backend no permite volver a abrir la caja
 * ni editarla, así que el botón final pide confirmación con la diferencia a la
 * vista.
 */
export function RegisterCloseDialog({ open, onOpenChange, onClosed }) {
  const { user, shop } = useContext(UserContext) || {};
  const confirm = useConfirmDialog();
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [closing, setClosing] = useState(false);

  const allowed = canCloseRegister(user);
  const shopId = localStorage.getItem("shop_id");
  const exerciseId = localStorage.getItem("exercise_id");

  const { data: openRegister, isLoading: loadingRegister } = useQuery({
    queryKey: ["openRegister", shopId, exerciseId],
    queryFn: () => getOpenRegister({ shopId, exerciseId }),
    enabled: open && allowed,
  });

  const registerId = openRegister?.id ?? null;

  const { data: arqueo, isLoading: loadingArqueo } = useQuery({
    queryKey: ["registerArqueo", registerId],
    queryFn: () => getRegisterArqueo(registerId, shopId),
    // La caja cambia con cada venta del TPV: sin esto se vería la cache de la
    // última visita y el efectivo esperado parecería congelado.
    refetchOnMount: "always",
    enabled: open && allowed && registerId !== null,
  });

  // El conteo se reinicia cada vez que se abre: el dinero contado a medias no
  // debe heredarse al siguiente arqueo.
  useEffect(() => {
    if (!open) return;
    setCounted("");
    setNotes("");
  }, [open, registerId]);

  const expected = Number(arqueo?.expected_cash || 0);
  const countedValue = counted === "" ? null : Number(counted);
  const countedValid = countedValue !== null && Number.isFinite(countedValue) && countedValue >= 0;
  const difference = countedValid ? Number((countedValue - expected).toFixed(2)) : null;

  const payments = useMemo(() => arqueo?.payments || [], [arqueo]);
  const loading = loadingRegister || loadingArqueo;

  async function handleClose() {
    if (!countedValid) {
      toast.error("Introduce el efectivo que has contado");
      return;
    }

    setClosing(true);
    try {
      // api.js no lanza ante un 4xx: devuelve el cuerpo del error.
      const result = await closeRegister(registerId, shopId, {
        counted_cash: countedValue,
        notes: notes.trim() || null,
      });

      if (result?.error) {
        toast.error(result.error);
        return;
      }

      const diff = Number(result?.cash_difference || 0);
      toast.success(
        Math.abs(diff) < 0.005
          ? "Caja cerrada correctamente"
          : `Caja cerrada · ${differenceLabel(diff).toLowerCase()} ${formatCurrency(Math.abs(diff))}`,
      );

      // El cierre ya no se puede deshacer, pero imprimir el acta sí conviene
      // hacerlo antes de refrescar la pantalla.
      printTicket(
        GenerateRegisterTicket(
          result,
          shop,
          shop?.ticket_settings?.paper_saving || false,
        ),
      );

      onClosed?.();
      onOpenChange(false);
    } catch {
      toast.error("No se ha podido cerrar la caja");
    } finally {
      setClosing(false);
    }
  }

  function askToClose() {
    const diff = difference ?? 0;
    confirm({
      title: "¿Cerrar la caja?",
      description:
        Math.abs(diff) < 0.005
          ? `El efectivo contado cuadra con lo esperado (${formatCurrency(expected)}). Una vez cerrada no se puede volver a abrir.`
          : `${differenceLabel(diff)} ${formatCurrency(Math.abs(diff))} respecto a lo esperado. Una vez cerrada la caja no se puede volver a abrir ni editar.`,
      okText: "Cerrar caja",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: handleClose,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !closing && onOpenChange(next)}>
      <DialogContent className="flex max-h-[90dvh] w-[min(32rem,calc(100vw-1.5rem))] max-w-none flex-col gap-4 overflow-hidden p-4">
        <DialogHeader className="shrink-0">
          <DialogTitle>Arqueo y cierre de caja</DialogTitle>
          <DialogDescription>
            Cuenta el efectivo del cajón y compáralo con lo que debería haber. Los
            cobros con tarjeta o transferencia no están en el cajón y no se suman.
          </DialogDescription>
        </DialogHeader>

        {/* La página de tickets va con select-none: sin select-text no se podría
            seleccionar el importe ni copiar las observaciones. */}
        <div className="min-h-0 flex-1 select-text space-y-4 overflow-y-auto overscroll-contain">
          {!allowed ? (
            <p className="py-6 text-center text-sm text-neutral-500">
              El cierre y el arqueo de caja no están incluidos en tu plan.
            </p>
          ) : loading || !arqueo ? (
            <p className="py-6 text-center text-sm text-neutral-500">
              Cargando la caja…
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-neutral-200 bg-white p-2 text-center">
                  <p className="text-[11px] text-neutral-500">Fondo</p>
                  <p className="mt-0.5 font-semibold text-neutral-900">
                    {formatCurrency(arqueo.opening_amount)}
                  </p>
                </div>
                <div className="border border-neutral-200 bg-white p-2 text-center">
                  <p className="text-[11px] text-neutral-500">Efectivo cobrado</p>
                  <p className="mt-0.5 font-semibold text-neutral-900">
                    {formatCurrency(arqueo.cash_collected)}
                  </p>
                </div>
                <div className="border border-blue-200 bg-blue-50 p-2 text-center">
                  <p className="text-[11px] text-blue-700">Debería haber</p>
                  <p className="mt-0.5 font-semibold text-blue-900">
                    {formatCurrency(expected)}
                  </p>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-sm font-medium text-neutral-700">
                  Cobros del turno
                </p>
                <div className="divide-y divide-neutral-200 border-y border-neutral-200">
                  {payments.length === 0 ? (
                    <p className="py-3 text-sm text-neutral-500">
                      No se ha registrado ningún cobro.
                    </p>
                  ) : (
                    payments.map((payment) => (
                      <div
                        key={payment.tipopago_id}
                        className="flex min-h-12 items-center justify-between gap-3 py-2"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <Icon
                            icon={
                              payment.is_cash
                                ? "mdi:cash"
                                : "mdi:credit-card-outline"
                            }
                            className="text-neutral-400"
                          />
                          <span className="truncate text-sm text-neutral-700">
                            {payment.name}
                          </span>
                          <span className="text-xs text-neutral-400">
                            ({payment.payment_count})
                          </span>
                        </span>
                        <span className="shrink-0 font-semibold text-neutral-900">
                          {formatCurrency(payment.total)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-sm font-medium text-neutral-700">
                  Efectivo contado
                </p>
                <div className="border border-neutral-300 bg-white py-2 text-center">
                  <p
                    className={`font-extrabold tabular-nums leading-tight ${
                      countedValid ? "text-neutral-900" : "text-neutral-300"
                    } text-[clamp(1.75rem,6dvh,2.5rem)]`}
                  >
                    {countedValid ? formatCurrency(countedValue) : "0.00€"}
                  </p>
                </div>
              </div>

              {difference !== null && (
                <div
                  className={`border p-3 text-sm ${
                    Math.abs(difference) < 0.005
                      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                      : difference < 0
                        ? "border-red-200 bg-red-50 text-red-900"
                        : "border-amber-200 bg-amber-50 text-amber-900"
                  }`}
                >
                  <p className="font-semibold">
                    {differenceLabel(difference)}{" "}
                    {Math.abs(difference) >= 0.005 && formatCurrency(Math.abs(difference))}
                  </p>
                  <p className="mt-0.5 tabular-nums">
                    {formatCurrency(countedValue)} − {formatCurrency(expected)} ={" "}
                    {formatCurrency(difference)}
                  </p>
                </div>
              )}

              <div>
                <p className="mb-1.5 text-sm font-medium text-neutral-700">
                  Observaciones
                </p>
                <Textarea
                  rows={2}
                  placeholder="Ej: cambio para la caja, incidencia en el cobro…"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>

              <AmountKeypad
                expected={expected}
                onChange={setCounted}
                disabled={closing}
              />
            </>
          )}
        </div>

        {allowed && (
          <DialogFooter className="shrink-0">
            <Button
              type="button"
              variant="outline"
              disabled={closing}
              className="h-12"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={closing || loading || !countedValid}
              className="h-12 flex-1"
              onClick={askToClose}
            >
              {!closing && (
                <Icon icon="mdi:lock-check-outline" className="mr-2 text-xl" />
              )}
              {closing ? "Cerrando…" : "Cerrar caja"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}